import React from 'react';
import { styled } from '@linaria/react';

import { Chevron, User } from '../ui/icon';

export const AccountContainer = styled.div`
  display: inline-block;
`;

export const AccountButtonContainer = styled.button`
  display: flex;
  align-items: center;
  border-width: var(--bb-account-border-width);
  border-style: solid;
  border-color: var(--bb-account-border-color);
  border-radius: var(--bb-account-border-radius);
  background-color: var(--bb-account-background-color);
  box-shadow: var(--bb-account-box-shadow);
  color: var(--bb-account-text-color);
  padding-top: 0;
  padding-right: 0;
  padding-bottom: 0;
  padding-left: 0;
  outline: none;
  padding: 4px 8px 4px 4px;
  min-width: 172px;
  max-width: 200px;

  &:hover {
    cursor: pointer;
    border-width: var(--bb-account-border-width--hover);
    border-color: var(--bb-account-border-color--hover);
    background-color: var(--bb-account-background-color--hover);
    color: var(--bb-account-text-color--hover);
    box-shadow: var(--bb-account-box-shadow--hover);

    path {
      stroke: var(--bb-account-icon-color--hover);
    }
  }

  &:focus,
  &:active {
    cursor: pointer;
    border-width: var(--bb-account-border-width--focus);
    border-color: var(--bb-account-border-color--focus);
    background-color: var(--bb-account-background-color--focus);
    color: var(--bb-account-text-color--focus);
    box-shadow: var(--bb-account-box-shadow--focus);
  }

  @media all and (max-width: 600px) {
    min-width: auto;
  }
`;

const AccountButtonImage = styled.img`
  display: block;
  margin-right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
`;

const AccountButtonPlugImage = styled.div`
  display: block;
  margin-right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #f4f7fa;
`;

const AccountButtonName = styled.div`
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 16px;
  line-height: 20px;
  padding-right: 8px;
  font-weight: 700;

  @media all and (max-width: 600px) {
    display: none;
  }
`;

export const AccountDropdownArrow = styled.div<{ active: boolean }>`
  margin-left: auto;
  transition: 0.2s ease transform;
  transform: ${({ active }) => (active ? 'scaleY(-1)' : '')};
`;

export const AccountButtonPlug: React.FC<
  { active: boolean } & React.HTMLAttributes<HTMLButtonElement>
> = ({ active, ...rest }) => {
  return (
    <AccountButtonContainer {...rest}>
      <AccountButtonPlugImage>
        <User />
      </AccountButtonPlugImage>
      <AccountButtonName>My account</AccountButtonName>
      <AccountDropdownArrow active={active}>
        <Chevron
          width="18"
          height="18"
        />
      </AccountDropdownArrow>
    </AccountButtonContainer>
  );
};

export const AccountButton: React.FC<
  { email: string; avatar_url: string; active: boolean } & React.HTMLAttributes<HTMLButtonElement>
> = ({ email, avatar_url, active, ...rest }) => {
  return (
    <AccountButtonContainer {...rest}>
      <AccountButtonImage src={avatar_url} />
      <AccountButtonName>{email}</AccountButtonName>
      <AccountDropdownArrow active={active}>
        <Chevron
          width="18"
          height="18"
        />
      </AccountDropdownArrow>
    </AccountButtonContainer>
  );
};

export const AccountList = styled.div`
  min-width: 200px;
`;

export const AccountLink = styled.a`
  display: block;
  text-decoration: none;
  color: var(--bb-account-link-color);
  background-color: var(--bb-account-link-background-color);
  padding: 10px 12px 10px 38px;
  font-size: 16px;
  line-height: 24px;

  &:hover {
    cursor: pointer;
    background-color: var(--bb-account-link-background-color--hover);
  }
`;

export const AccountLinkWithIcon = styled.a`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: var(--bb-account-link-color);
  padding: 10px 12px 10px 12px;
  font-size: 16px;
  line-height: 24px;
  border-bottom: 1px solid #efefef;
  font-weight: bold;
  background-color: var(--bb-account-link-background-color);

  &:hover {
    cursor: pointer;
    background-color: var(--bb-account-link-background-color--hover);
  }

  & svg {
    margin-right: 8px;
  }

  & path {
    fill: var(--bb-account-link-color);
  }
`;

export const AccountEmptyButton = styled.button`
  background-color: transparent;
  text-decoration: none;
  padding: 10.5px 42px;
  border-width: var(--bb-account-border-width);
  border-style: solid;
  border-color: var(--bb-account-empty-border-color);
  border-radius: var(--bb-account-border-radius);
  background-color: var(--bb-account-empty-background-color);
  color: var(--bb-account-empty-text-color);
  box-shadow: var(--bb-account-box-shadow);
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  outline: none;
  font-family: inherit;
  display: flex;

  &:hover {
    cursor: pointer;
    border-width: var(--bb-account-border-width--hover);
    border-color: var(--bb-account-border-color--hover);
    background-color: var(--bb-account-empty-background-color--hover);
    color: var(--bb-account-empty-text-color--hover);
    box-shadow: var(--bb-account-box-shadow--hover);
  }

  &:focus,
  &:active {
    cursor: pointer;
    border-width: var(--bb-account-border-width--focus);
    border-color: var(--bb-account-border-color--focus);
    background-color: var(--bb-account-empty-background-color--focus);
    color: var(--bb-account-empty-text-color--focus);
    box-shadow: var(--bb-account-box-shadow--focus);
  }

  @media all and (max-width: 600px) {
    padding: 4px;
  }
`;

export const AccountEmptyLink = styled.a`
  text-decoration: none;
  background-color: transparent;
  padding: 10.5px 42px;
  border: 2px solid #027ac6;
  color: #027ac6;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  border-radius: 100px;
  outline: none;
  font-family: inherit;
  display: flex;

  &:hover {
    cursor: pointer;
    background-color: #027ac6;
    color: #fff;
  }

  @media all and (max-width: 600px) {
    padding: 4px;
  }
`;

export const AccountEmptyButtonIcon = styled.div`
  display: none;

  @media all and (max-width: 600px) {
    display: block;

    &:hover path {
      fill: #fff;
    }
  }
`;

export const AccountEmptyButtonText = styled.div`
  display: block;

  @media all and (max-width: 600px) {
    display: none;
  }
`;
